
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { ShieldCheck, Users, Settings, Lock, Trash2, RefreshCw, LineChart, Image as ImageIcon, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const AdminPanel = () => {
  const [loading, setLoading] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [imageCount, setImageCount] = useState(0);
  const [albumCount, setAlbumCount] = useState(0);
  const [storageUsed, setStorageUsed] = useState(0);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [systemStatus, setSystemStatus] = useState({
    cpu: Math.floor(Math.random() * 30) + 10, // Simulated CPU usage between 10-40%
    memory: Math.floor(Math.random() * 40) + 20, // Simulated memory usage between 20-60%
    uptime: "4d 6h 23m" // Simulated uptime
  });

  useEffect(() => {
    fetchAdminStats();
  }, []);

  const fetchAdminStats = async () => {
    setLoading(true);
    try {
      // Fetch user count
      const { count: userCountData, error: userError } = await supabase
        .from('user_roles')
        .select('*', { count: 'exact', head: true });
      
      if (userError) throw userError;
      setUserCount(userCountData || 0);

      // Fetch image count
      const { count: imageCountData, error: imageError } = await supabase
        .from('images')
        .select('*', { count: 'exact', head: true });
      
      if (imageError) throw imageError;
      setImageCount(imageCountData || 0);

      // Fetch album count
      const { count: albumCountData, error: albumError } = await supabase
        .from('albums')
        .select('*', { count: 'exact', head: true });
      
      if (albumError) throw albumError;
      setAlbumCount(albumCountData || 0);

      // Fetch recent users (10 most recent)
      const { data: recentUsersData, error: recentUsersError } = await supabase
        .from('user_roles')
        .select('user_id, role, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (recentUsersError) throw recentUsersError;
      setRecentUsers(recentUsersData || []);

      // Simulate storage used based on number of images (just for demo purposes)
      // In a real app, you would calculate this from actual file sizes
      const avgImageSize = 2.5; // MB
      // Fix: Convert the calculated string value to a number before setting state
      const calculatedStorage = Number((imageCountData || 0) * avgImageSize).toFixed(1);
      setStorageUsed(Number(calculatedStorage)); // Convert string to number

    } catch (error) {
      console.error('Error fetching admin stats:', error);
      toast.error('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = () => {
    setLoading(true);
    // Simulate cache clearing
    setTimeout(() => {
      toast.success("Cache cleared successfully");
      setLoading(false);
    }, 1000);
  };

  const handleSystemRestart = () => {
    setLoading(true);
    toast.loading("Restarting system...", {
      duration: 3000,
      onAutoClose: () => {
        toast.success("System restarted successfully");
        setLoading(false);
        // Simulate refreshed stats
        fetchAdminStats();
      }
    });
  };

  const handlePurgeUnusedImages = async () => {
    setLoading(true);
    toast.loading("Analyzing unused images...", { duration: 2000 });
    
    try {
      // In a real app, you would identify and delete unused images
      // For demo purposes, we'll just show a success message
      setTimeout(() => {
        toast.success("Unused image analysis complete. No unused images found.");
        setLoading(false);
      }, 2000);
    } catch (error) {
      console.error('Error purging unused images:', error);
      toast.error('Failed to purge unused images');
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    return `${bytes} MB`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Admin Dashboard</h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchAdminStats}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white/70">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              <Users className="inline h-3 w-3 mr-1" />
              Registered accounts
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white/70">Images</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{imageCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              <ImageIcon className="inline h-3 w-3 mr-1" />
              Total uploaded
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white/70">Albums</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{albumCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              <FolderOpen className="inline h-3 w-3 mr-1" />
              Created collections
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white/70">Storage Used</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatBytes(Number(storageUsed))}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Estimated usage
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="system" className="space-y-4">
        <TabsList>
          <TabsTrigger value="system">System</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
        </TabsList>
        
        <TabsContent value="system" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>System Status</CardTitle>
              <CardDescription>Current resource usage and uptime information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">CPU Usage</span>
                  <span className="text-sm">{systemStatus.cpu}%</span>
                </div>
                <Progress value={systemStatus.cpu} className="h-2" />
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">Memory Usage</span>
                  <span className="text-sm">{systemStatus.memory}%</span>
                </div>
                <Progress value={systemStatus.memory} className="h-2" />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">System Uptime</span>
                <Badge variant="outline">{systemStatus.uptime}</Badge>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  variant="default" 
                  onClick={handleClearCache}
                  disabled={loading}
                >
                  Clear Cache
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleSystemRestart}
                  disabled={loading}
                >
                  Restart System
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => toast.success("Admin function executed successfully!")}>
                <ShieldCheck className="h-4 w-4 mr-2" />
                Execute Admin Function
              </Button>
              <Button variant="outline" onClick={() => toast.success("Database backup started")}>
                <Lock className="h-4 w-4 mr-2" />
                Backup Database
              </Button>
              <Button variant="outline" onClick={() => toast.info("Would open site settings in a real app")}>
                <Settings className="h-4 w-4 mr-2" />
                Site Settings
              </Button>
              <Button variant="outline" onClick={() => toast.info("Would open user management in a real app")}>
                <Users className="h-4 w-4 mr-2" />
                Manage Users
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Users</CardTitle>
              <CardDescription>New users who have registered recently</CardDescription>
            </CardHeader>
            <CardContent>
              {recentUsers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User ID</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Registered</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentUsers.map((user, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{user.user_id.substring(0, 8)}...</TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-4 text-center">
                  <p className="text-white/60">No users found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="maintenance" className="space-y-4">
          <Alert>
            <AlertTitle>Maintenance Mode</AlertTitle>
            <AlertDescription>
              These actions perform system maintenance. Use them carefully.
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardHeader>
              <CardTitle>Storage Maintenance</CardTitle>
              <CardDescription>Clean up unused files and optimize storage</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-4">
                <Button 
                  variant="destructive" 
                  onClick={handlePurgeUnusedImages}
                  disabled={loading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clean Unused Images
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={() => toast.info("Would optimize database in a real app")}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Optimize Database
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPanel;
